/**
 * Apply Find & Replace hits to section HTML. Pure — callers feed updates
 * back to state via store.update.
 *
 * Revalidation strategy:
 *   Each hit carries STABLE coordinates from the search pass:
 *     - textNodeOrdinal (DOM-order index of the text node in walkTextNodes)
 *     - matchStart, matchEnd (offsets within that text node)
 *     - beforeCtx, match, afterCtx (32-char windows for drift detection)
 *   Before mutating, this module re-parses the current section.html and
 *   verifies the (matchStart..matchEnd) slice equals `match` AND the
 *   surrounding context windows still align. If they don't (because the
 *   user edited the iframe between search and replace), the hit is
 *   silently skipped and counted in `skipped`. Never silently replaces
 *   the wrong duplicate.
 *
 * Mutation order:
 *   Within a single text node, hits are processed in REVERSE offset order
 *   (matchEnd descending) so earlier wraps don't shift later offsets.
 */

import { walkTextNodes, wrapTextSlice } from './text-walker.js';
import { iterateAllSections } from './iterate-sections.js';
import { computeFingerprint } from './section-text-search.js';

/**
 * Apply hits to a single section's HTML. Returns { newHtml, applied, skipped }.
 *
 * opts:
 *   - bindKey (default null) — when set, wraps each replacement in
 *     <span data-pb-client="bindKey"> so future Client Info edits cascade
 *   - regex (default false) — interpret backrefs ($1..$9, $&, $$) in
 *     `replacement` via native String.replace semantics. Mirror the option
 *     name from section-text-search.js so callers can forward one opts blob.
 *   - caseSensitive / wholeWord — needed to rebuild the regex for backrefs
 *   - includeFormText — must match the value passed to search() so text-node
 *     ordinals line up
 */
export function applyReplacementsToSection(html, hits, query, replacement, opts = {}) {
  if (typeof html !== 'string' || !html) return { newHtml: html || '', applied: 0, skipped: 0 };
  if (!Array.isArray(hits) || hits.length === 0) return { newHtml: html, applied: 0, skipped: 0 };
  if (typeof DOMParser === 'undefined') return { newHtml: html, applied: 0, skipped: hits.length };

  const doc = new DOMParser().parseFromString('<div>' + html + '</div>', 'text/html');
  const root = doc.body.firstElementChild;
  if (!root) return { newHtml: html, applied: 0, skipped: hits.length };

  // Walk text nodes once and index by ordinal so we can look up each hit's
  // target without re-walking. Find & Replace search includes bound text so
  // ordinals line up with the rendered result list; bound hits are still
  // read-only and skipped below.
  const nodesByOrdinal = [];
  walkTextNodes(
    root,
    (textNode) => {
      nodesByOrdinal.push(textNode);
    },
    { includeFormText: !!opts.includeFormText, includeBoundText: true }
  );

  // Group hits by textNodeOrdinal so we can rebuild each text node's content
  // atomically — N hits in one node → 1 replaceChild, not N (which would
  // detach the node after the first wrap and skip the rest).
  const hitsByOrdinal = new Map();
  for (const hit of hits) {
    if (!hitsByOrdinal.has(hit.textNodeOrdinal)) hitsByOrdinal.set(hit.textNodeOrdinal, []);
    hitsByOrdinal.get(hit.textNodeOrdinal).push(hit);
  }

  let applied = 0;
  let skipped = 0;
  const matcher = opts.regex ? buildRegex(query, opts) : null;

  for (const [ordinal, ordHits] of hitsByOrdinal) {
    const node = nodesByOrdinal[ordinal];
    if (!node || node.nodeType !== 3 || !node.parentNode) {
      skipped += ordHits.length;
      continue;
    }
    const text = node.nodeValue || '';
    // Sort ASCENDING by matchStart for the rope-build pass. Drop hits whose
    // signature doesn't align with the current text — that's how stale hits
    // (e.g. user typed in the iframe between search and replace) get skipped.
    const validated = [];
    for (const hit of ordHits) {
      if (hit.isInsideBinding) {
        skipped++;
        continue;
      }
      if (text.slice(hit.matchStart, hit.matchEnd) !== hit.match) {
        skipped++;
        continue;
      }
      const beforeOk =
        text.slice(Math.max(0, hit.matchStart - hit.beforeCtx.length), hit.matchStart) ===
        hit.beforeCtx;
      const afterOk = text.slice(hit.matchEnd, hit.matchEnd + hit.afterCtx.length) === hit.afterCtx;
      if (!beforeOk || !afterOk) {
        skipped++;
        continue;
      }
      validated.push(hit);
    }
    if (!validated.length) continue;
    validated.sort((a, b) => a.matchStart - b.matchStart);
    // Drop overlapping hits (defensive — search shouldn't produce these for
    // non-zero-length matches, but be safe).
    const nonOverlapping = [validated[0]];
    for (let i = 1; i < validated.length; i++) {
      if (validated[i].matchStart >= nonOverlapping[nonOverlapping.length - 1].matchEnd) {
        nonOverlapping.push(validated[i]);
      } else {
        skipped++;
      }
    }

    // Build the new fragment in ONE pass: alternating unchanged text segments
    // and (text-node OR <span data-pb-client>) for each hit. replaceChild once
    // at the end. This avoids the detach-after-first-wrap problem entirely.
    const frag = doc.createDocumentFragment();
    let cursor = 0;
    for (const hit of nonOverlapping) {
      if (hit.matchStart > cursor) {
        frag.appendChild(doc.createTextNode(text.slice(cursor, hit.matchStart)));
      }
      const finalReplacement =
        opts.regex && matcher
          ? hit.match.replace(matcher, replacement)
          : String(replacement == null ? '' : replacement);
      if (opts.bindKey) {
        const span = doc.createElement('span');
        span.setAttribute('data-pb-client', opts.bindKey);
        span.textContent = finalReplacement;
        frag.appendChild(span);
      } else if (finalReplacement) {
        frag.appendChild(doc.createTextNode(finalReplacement));
      }
      cursor = hit.matchEnd;
      applied++;
    }
    if (cursor < text.length) {
      frag.appendChild(doc.createTextNode(text.slice(cursor)));
    }
    node.parentNode.replaceChild(frag, node);
  }

  if (applied === 0) return { newHtml: html, applied: 0, skipped };
  return { newHtml: root.innerHTML, applied, skipped };
}

/**
 * Apply hits across the entire state. `searchResults` is the output of
 * search() — an array of { sectionPath, hits, queryFingerprint, ... }.
 *
 * Returns an array of { path, html, instanceId } updates the caller can
 * pass to store.update inside a single transaction. Also surfaces a
 * `staleCount` for the UI's "X hits became stale" message.
 *
 * opts is forwarded to applyReplacementsToSection.
 *
 * `query` and `opts` are the SAME query/opts used to produce the results.
 * They must be supplied so we can rebuild the regex for backref expansion
 * and so we can fingerprint-check each result against the live invocation.
 */
export function applyReplacementsAcrossState(state, searchResults, query, replacement, opts = {}) {
  const updates = [];
  let appliedTotal = 0;
  let skippedTotal = 0;

  if (!state || !Array.isArray(searchResults) || !searchResults.length) {
    return { updates, applied: 0, skipped: 0 };
  }

  const callFingerprint = computeFingerprint(query, opts);
  const bySection = new Map();
  for (const r of searchResults) {
    if (r.queryFingerprint && r.queryFingerprint !== callFingerprint) {
      // Stale results from a different query — skip entire result block.
      skippedTotal += (r.hits || []).length;
      continue;
    }
    if (!bySection.has(r.sectionPath)) bySection.set(r.sectionPath, []);
    bySection.get(r.sectionPath).push(...r.hits);
  }

  iterateAllSections(state, (ctx) => {
    const hits = bySection.get(ctx.sectionPath);
    if (!hits || hits.length === 0) return;
    const { newHtml, applied, skipped } = applyReplacementsToSection(
      ctx.html,
      hits,
      query,
      replacement,
      opts
    );
    appliedTotal += applied;
    skippedTotal += skipped;
    if (newHtml !== ctx.html) {
      updates.push({ path: ctx.sectionPath, html: newHtml, instanceId: ctx.instanceId });
    }
  });

  return { updates, applied: appliedTotal, skipped: skippedTotal };
}

function buildRegex(query, { caseSensitive, wholeWord, regex }) {
  const flags = 'g' + (caseSensitive ? '' : 'i');
  if (regex) return new RegExp(query, flags);
  const escaped = String(query || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(wholeWord ? '\\b' + escaped + '\\b' : escaped, flags);
}
